import React, { useMemo } from "react";

import type {
  ProcessedPickemEntry,
  ProcessedPickemSelection,
} from "@/hooks/firestore/betting/useEntries";

import { PickemPick } from "./PickemPick";

interface PickemSelectionsProps {
  entry: ProcessedPickemEntry;
}

const canBeConnected = (selection: ProcessedPickemSelection) => {
  const isRace = selection.event.eventType === "RACE";
  const replaced = selection.replacementOutcome;
  const isVoid = selection.result === "VOID" && (!isRace || !replaced);
  return (replaced || selection).result === "WIN" || isVoid;
};

const PickemSelections = ({ entry }: PickemSelectionsProps) => {
  const isEntryWon =
    entry.data.status === "WON" || entry.data.status === "PLACED";

  const sortedSelections = useMemo(
    () =>
      entry.data.selections.sort((a, b) =>
        a?.event?.scheduledStartTime < b?.event?.scheduledStartTime ? -1 : 1,
      ),
    [entry.data.selections],
  );

  return (
    <>
      {sortedSelections.map((selection, index) => {
        const isLast = index === sortedSelections.length - 1;

        const shouldConnect = canBeConnected(selection);
        const connectUp =
          shouldConnect &&
          index > 0 &&
          canBeConnected(sortedSelections[index - 1]);
        const connectDown =
          shouldConnect &&
          (isLast ? isEntryWon : canBeConnected(sortedSelections[index + 1]));

        return (
          <PickemPick
            key={`${entry.id}-${selection.outcomeId}`}
            entryId={entry.id}
            selection={selection}
            isFirst={index === 0}
            isLast={isLast}
            isEntryWon={isEntryWon}
            connectUp={connectUp}
            connectDown={connectDown}
          />
        );
      })}
    </>
  );
};

export { PickemSelections };
